/**
 * UserAdoptionDashboard Component
 * Composition component for the full user adoption report
 *
 * Story 5.5: User Adoption Report Page Assembly
 */

import React from 'react';
import { clsx } from 'clsx';
import { RefreshCw } from 'lucide-react';
import { Button } from '../../common';
import type { DateRange } from '../DateRangeSelector';
import { UserAdoptionReportSkeleton } from '../ReportLoadingSkeleton';
import { ReportErrorState } from '../ReportEmptyState';
import { toReportFilterParams, type ReportFilters } from '../ReportFilterPanel';
import { AdoptionSummaryCard } from './AdoptionSummaryCard';
import { ActivationFunnelChart } from './ActivationFunnelChart';
import { TimeToFirstOKRChart } from './TimeToFirstOKRChart';
import { FeatureDiscoveryCard } from './FeatureDiscoveryCard';
import { RetentionMetricsCard } from './RetentionMetricsCard';
import { useUserAdoptionReportData } from '../../../hooks/useReportData';
import { format } from 'date-fns';

export interface UserAdoptionDashboardProps {
  /** Date range for the report (controlled by parent) */
  dateRange: DateRange;
  /** Department / team / owner filters, owned by ReportsPage; every card narrows to the matching people. */
  filters?: ReportFilters;
  /** Click handler for user */
  onUserClick?: (userId: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * User Adoption Dashboard Component
 */
export const UserAdoptionDashboard: React.FC<UserAdoptionDashboardProps> = ({
  dateRange,
  filters,
  onUserClick,
  className,
}) => {
  // Format date range params for API calls
  const dateRangeParams = {
    startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
    endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
  };

  // Fetch adoption report data
  const adoptionData = useUserAdoptionReportData(dateRangeParams, toReportFilterParams(filters));

  // Handle refresh
  const handleRefresh = () => {
    adoptionData.adoptionSummary.refetch();
    adoptionData.activationFunnel.refetch();
    adoptionData.timeToFirstOKR.refetch();
    adoptionData.featureDiscovery.refetch();
    adoptionData.retentionMetrics.refetch();
  };

  // Check for loading state (initial load only)
  const isInitialLoading = adoptionData.isLoading && !adoptionData.adoptionSummary.data;

  if (isInitialLoading) {
    return (
      <div className={className}>
        <UserAdoptionReportSkeleton />
      </div>
    );
  }

  // Check for error state
  if (adoptionData.isError) {
    return (
      <div className={className}>
        <ReportErrorState
          error={adoptionData.error instanceof Error ? adoptionData.error : new Error('Failed to load adoption report')}
          onRetry={handleRefresh}
        />
      </div>
    );
  }

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Header with refresh button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900">User Adoption Report</h2>
          <p className="text-stone-600 mt-1">Track user onboarding, activation, and retention</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={adoptionData.isLoading}
        >
          <RefreshCw className={clsx('w-4 h-4 mr-2', adoptionData.isLoading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Adoption Summary */}
      {adoptionData.adoptionSummary.data && (
        <AdoptionSummaryCard
          data={adoptionData.adoptionSummary.data}
          isLoading={adoptionData.adoptionSummary.isLoading}
        />
      )}

      {/* Activation Funnel and Time to First OKR */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {adoptionData.activationFunnel.data && (
          <ActivationFunnelChart
            data={adoptionData.activationFunnel.data}
            isLoading={adoptionData.activationFunnel.isLoading}
            onUserClick={onUserClick}
          />
        )}

        {adoptionData.timeToFirstOKR.data && (
          <TimeToFirstOKRChart
            data={adoptionData.timeToFirstOKR.data}
            isLoading={adoptionData.timeToFirstOKR.isLoading}
            onUserClick={onUserClick}
          />
        )}
      </div>

      {/* Feature Discovery and Retention Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {adoptionData.featureDiscovery.data && (
          <FeatureDiscoveryCard
            data={adoptionData.featureDiscovery.data}
            isLoading={adoptionData.featureDiscovery.isLoading}
          />
        )}

        {adoptionData.retentionMetrics.data && (
          <RetentionMetricsCard
            data={adoptionData.retentionMetrics.data}
            isLoading={adoptionData.retentionMetrics.isLoading}
            onUserClick={onUserClick}
          />
        )}
      </div>
    </div>
  );
};

export default UserAdoptionDashboard;
